/**
 * The polyestershoppen.nl API answers errors in several shapes. API v3 has a stable `code` in each of its own: a
 * `CodedError` (`{error, code}`) or a `ValidationProblem` (422, `{code: 'validation_failed', detail, violations}`), its
 * `error` and `detail` in English. The errors from outside an endpoint (authentication, the proxy) and the older
 * endpoints have `{error}`, a bare string or Symfony's own exception body (`{title, detail}`). These helpers get a
 * message to show from any of them: a known code in the terminal's language, else the API's text.
 */
import type {AxiosResponse} from 'axios';
import axios from 'axios';

import type {MessageKey} from './i18n';
import {t} from './i18n';

/** An error body of API v3 (`CodedError`), with the fields the terminal uses. */
export type CodedError = {
    /** in English, for logging: act on `code` */
    error: string;
    /** stable, but an open set: an unknown one is handled by the status code alone */
    code: string;
    /** a delivery address the carrier refused: the fields to correct, as `PUT .../delivery-address` names them */
    fields?: string[];
};

/** A 422 of API v3: the request body or query string is not valid. */
export type ValidationProblem = {
    code: 'validation_failed';
    /** the violations, one per line, in English */
    detail: string;
    violations: {propertyPath: string; title: string}[];
};

/** The codes of the carrier refusing the delivery address, which the operator corrects (see `AddressErrorModal`). */
export const addressErrorCodes = new Set([
    'recipient_name_invalid',
    'postal_code_invalid',
    'house_number_invalid',
    'phone_invalid',
    'email_invalid',
    'address_invalid',
]);

/** The messages of the codes the terminal can get; `{message}` is the API's `error` (e.g. the carrier's reason). */
const codeMessages: Partial<Record<string, MessageKey>> = {
    not_found: 'apiError.notFound',
    printer_not_found: 'apiError.printerNotFound',
    personal_account_required: 'apiError.personalAccountRequired',
    validation_failed: 'apiError.validationFailed',
    international_shipment: 'apiError.internationalShipment',
    ships_with_parent_order: 'apiError.shipsWithParentOrder',
    too_early_for_carrier: 'apiError.tooEarlyForCarrier',
    order_already_sent: 'apiError.orderAlreadySent',
    order_not_paid: 'apiError.orderNotPaid',
    vat_number_not_validated: 'apiError.vatNumberNotValidated',
    shipping_date_in_future: 'apiError.shippingDateInFuture',
    wrong_packing_station: 'apiError.wrongPackingStation',
    payment_not_found: 'apiError.paymentNotFound',
    payment_authorization_lost: 'apiError.paymentAuthorizationLost',
    customer_invoice_missing: 'apiError.customerInvoiceMissing',
    cargo_shipping_method: 'apiError.cargoShippingMethod',
    not_cargo_shipping_method: 'apiError.notCargoShippingMethod',
    order_has_shipments: 'apiError.orderHasShipments',
    order_has_no_shipments: 'apiError.orderHasNoShipments',
    recipient_name_invalid: 'apiError.recipientNameInvalid',
    postal_code_invalid: 'apiError.postalCodeInvalid',
    house_number_invalid: 'apiError.houseNumberInvalid',
    phone_invalid: 'apiError.phoneInvalid',
    email_invalid: 'apiError.emailInvalid',
    address_invalid: 'apiError.addressInvalid',
    service_point_invalid: 'apiError.servicePointInvalid',
    label_rejected: 'apiError.labelRejected',
    adr_configuration_missing: 'apiError.adrConfigurationMissing',
    shipment_failed: 'apiError.shipmentFailed',
    labels_not_printed: 'apiError.labelsNotPrinted',
    order_busy: 'apiError.orderBusy',
    request_busy: 'apiError.orderBusy',
    payment_provider_unavailable: 'apiError.paymentProviderUnavailable',
};

/** A body that is an HTML page (of the proxy in front of the API, despite `Accept`), not an error of the API itself. */
const isHtml = (data: unknown): boolean => 'string' === typeof data && /^\s*</.test(data);

/**
 * The response body of a failed request, to show: a request without a response (offline, timeout) gives `{message}`,
 * an HTML page its status, and a rejected token (401, not of the login itself) says the session expired.
 */
export const getErrorData = (error: unknown): unknown => {
    if (axios.isAxiosError(error)) {
        const {response, config} = error;

        if (!response) {
            return {message: error.message};
        }

        if (401 === response.status && !String(config?.url).startsWith('/api/v2/token')) {
            return {error: t('apiError.sessionExpired')};
        }

        if (isHtml(response.data) || null == response.data || '' === response.data) {
            return {message: `${response.status} ${response.statusText}`.trim()};
        }

        return response.data;
    }

    return {message: error instanceof Error ? error.message : String(error)};
};

/** How many seconds to wait before trying again, as the API says with a 503 (`Retry-After`). */
export const getRetryAfter = (error: unknown): number | undefined => {
    const seconds = Number(axios.isAxiosError(error) ? error.response?.headers['retry-after'] : undefined);

    return 0 < seconds ? seconds : undefined;
};

/** The `code` of an error body of API v3, if it has one. */
export const getErrorCode = (data: unknown): string | undefined => {
    const code = data && 'object' === typeof data ? (data as {code?: unknown}).code : undefined;

    return 'string' === typeof code ? code : undefined;
};

/** The API's own text of an error body (see `getErrorData`), untranslated: `null` when it has none. */
const getErrorText = (data: unknown): string | null => {
    if ('string' === typeof data) {
        return '' === data ? null : data;
    }

    if (data && 'object' === typeof data) {
        const {error, message, detail, title} = data as Record<string, unknown>;
        const text = [error, message, detail, title].find(value => 'string' === typeof value && '' !== value);

        return 'string' === typeof text ? text : JSON.stringify(data);
    }

    return null;
};

/** A message for an error body (see `getErrorData`) in any of the API's shapes: a known code in the terminal's language. */
export const getErrorMessage = (data: unknown): string => {
    const key = codeMessages[getErrorCode(data) ?? ''];
    const text = getErrorText(data);

    if (key) {
        return t(key, {message: text ?? ''});
    }

    return text ?? t('common.unknownError');
};

/** What went wrong with a request, for the developers: the API's own text (in English for v3), not a translation. */
export const getReportText = (error: unknown): string => {
    if (axios.isAxiosError(error) && error.response && !isHtml(error.response.data)) {
        return getErrorText(error.response.data) ?? error.message;
    }

    return error instanceof Error ? error.message : String(error);
};

/**
 * How a shipping action (printing, removing the shipments, a manco) that failed ended, for what the operator may do next:
 *
 * - `failed`: the API refused it and nothing was done (a 4xx, or Mollie unreachable: a 503 with
 *   `payment_provider_unavailable`), so trying again is safe;
 * - `printFailed`: the labels were created at the carrier but not printed (a 502 `labels_not_printed`, or a 409
 *   `order_has_shipments` for the labels of an earlier attempt), the order is still paid: the shipments have to be
 *   removed before it prints again;
 * - `busy`: another shipping action for the same order is still underway (a 503 `order_busy` or `request_busy`, e.g.
 *   from another terminal, or a request that timed out here but still runs at the API): this one did nothing, but that
 *   one may still do it, so the order has to be checked once it is done;
 * - `unknown`: no answer (offline, the timeout) or a server error (a 500, or a 502, 503 or 504 of the proxy in front of
 *   the API): it may have been done or not, the order has to be checked before trying again.
 */
export type Outcome = 'failed' | 'printFailed' | 'busy' | 'unknown';

/** A failed shipping action: how it ended (see `Outcome`) and the message of the error. */
export interface Failure {
    outcome: Outcome;
    message: string;
    /** for `busy`: the seconds to wait before trying again, when the API says so */
    retryAfter?: number;
}

export const getOutcome = (error: unknown): Outcome => {
    // (not the request that failed but the handling of its response: it may well have been done)
    if (!axios.isAxiosError(error)) {
        return 'unknown';
    }

    const {status, data} = error.response ?? {};

    if (undefined === status) {
        return 'unknown';
    }

    switch (getErrorCode(data)) {
        case 'labels_not_printed':
        case 'order_has_shipments':
            return 'printFailed';
        case 'order_busy':
        case 'request_busy':
            return 'busy';
        case 'payment_provider_unavailable':
            return 'failed';
    }

    return 500 <= status ? 'unknown' : 'failed';
};

export const getFailure = (error: unknown): Failure => ({
    outcome: getOutcome(error),
    message: getErrorMessage(getErrorData(error)),
    retryAfter: getRetryAfter(error),
});

/** An error body of API v3 with a code (not a validation problem), which the terminal may handle specifically. */
export const isCodedError = (data: unknown): data is CodedError =>
    !!data && 'object' === typeof data && 'string' === typeof (data as CodedError).error && !!getErrorCode(data);

export const isValidationProblem = (data: unknown): data is ValidationProblem =>
    'validation_failed' === getErrorCode(data) && Array.isArray((data as ValidationProblem).violations);

/** The messages of the warnings of a shipped order (`warnings` of a 201), for the operator. */
export const getShipWarnings = (response?: AxiosResponse): string[] => {
    const warnings: unknown = response?.data?.warnings;

    if (!Array.isArray(warnings)) {
        return [];
    }

    return warnings.flatMap(warning => {
        const {code, message} = (warning ?? {}) as {code?: unknown; message?: unknown};

        if ('manco_reported' === code) {
            return [t('shipping.warningMancoReported')];
        }

        return 'string' === typeof message && '' !== message ? [message] : [];
    });
};
